import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  useMediaQuery,
} from '@mui/material';
import { toast } from 'sonner';
import { captureException } from '@sentry/react';
import React, { useState, useEffect } from 'react';
import { getDownloadURL, ref } from 'firebase/storage';
import { Close as CloseIcon, Download as DownloadIcon } from '@mui/icons-material';

import { storage } from '../../firebase-config.js';
import type { DocumentFromStorage } from './DocumentManager.types.ts';

type FileType = 'PDF' | 'IMAGE' | 'UNSUPPORTED';

const getFileType = (fileName: string): FileType => {
  const extension = fileName.toLowerCase().split('.').pop();

  if (!extension) {
    return 'UNSUPPORTED';
  }

  if (extension === 'pdf') {
    return 'PDF';
  }

  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(extension)) {
    return 'IMAGE';
  }

  return 'UNSUPPORTED';
};

const PreviewContent: React.FC<{
  document: DocumentFromStorage;
  fileUrl: string;
  fileType: FileType;
}> = ({ document, fileUrl, fileType }) => {
  if (fileType === 'PDF') {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <iframe
          src={fileUrl}
          width="100%"
          height="100%"
          style={{ border: 'none' }}
          title={`Preview of ${document.fileName}`}
        />
      </Box>
    );
  }

  if (fileType === 'IMAGE') {
    return (
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          overflow: 'auto',
        }}
      >
        <img
          src={fileUrl}
          alt={document.fileName}
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain',
          }}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 2,
      }}
    >
      <Typography variant="h6">Preview Not Available</Typography>
      <Typography variant="body2" textAlign="center">
        This file type cannot be previewed. Please download the file to view it.
      </Typography>
    </Box>
  );
};

type DocumentPreviewModalProps = {
  open: boolean;
  onClosePreview: () => void;
  document: DocumentFromStorage | null;
  onDocumentDownload: undefined | ((documentId: string) => Promise<void>);
};

export const DocumentPreviewModal: React.FC<DocumentPreviewModalProps> = ({
  open,
  onClosePreview,
  document,
  onDocumentDownload,
}) => {
  const isSmallScreen = useMediaQuery('(max-width: 320px)');
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const fileType = document ? getFileType(document.fileName) : 'UNSUPPORTED';

  useEffect(() => {
    const loadFileUrl = async () => {
      if (!document || !open) {
        setFileUrl(null);

        return;
      }

      try {
        setIsLoading(true);

        // Use existing URL if available
        if (document.url) {
          setFileUrl(document.url);

          return;
        }

        // Get download URL from Firebase Storage
        const url = await getDownloadURL(ref(storage, document.fullPath));

        setFileUrl(url);
      } catch (err) {
        captureException(err, {
          tags: {
            issueIn: 'DocumentPreviewModal.loadFileUrl',
          },
          extra: { document },
        });
        toast.error('Failed to load document preview');
      } finally {
        setIsLoading(false);
      }
    };

    loadFileUrl();
  }, [document, open]);

  const handleDownload = async () => {
    if (!document || !onDocumentDownload) {
      return;
    }

    try {
      setIsDownloading(true);
      await onDocumentDownload(document.id);
    } catch (err) {
      captureException(err, {
        tags: {
          issueIn: 'DocumentPreviewModal.handleDownload',
        },
        extra: { document },
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const handleClose = () => {
    setFileUrl(null);
    onClosePreview();
  };

  if (!document) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      fullScreen={isSmallScreen}
      sx={{ paddingY: isSmallScreen ? 0 : 4 }}
      slotProps={{ paper: { sx: { height: '100%', maxHeight: '100%' } } }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>
            <Typography variant="h6" noWrap>
              {document.fileName}
            </Typography>
            {document.description && <Typography variant="body2">{document.description}</Typography>}
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          p: 2,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {isLoading && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {!isLoading && fileUrl && <PreviewContent document={document} fileUrl={fileUrl} fileType={fileType} />}
      </DialogContent>

      <DialogActions>
        {!!onDocumentDownload && (
          <Button startIcon={<DownloadIcon />} onClick={handleDownload} loading={isDownloading} variant="contained">
            Download
          </Button>
        )}

        <Button onClick={handleClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
